import { Spin } from "antd";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  LabelList,
  ReferenceLine,
  Cell,
  ResponsiveContainer,
} from "recharts";
import type { BarConfig } from "./BarChartCard";
import styles from "./LineChartCard.module.css";

export interface HorizontalStackedBarChartCardProps {
  title: string;
  data: Record<string, string | number>[];
  yKey: string;
  bars: BarConfig[];
  loading?: boolean;
  error?: string | null;
  xDomain?: [number, number | "auto"];
  tooltipSuffix?: string;
  stacked?: boolean;
  labelMinThreshold?: number;
  referenceValue?: number;
  referenceLabel?: string;
  referenceLines?: {
    value: number;
    label?: string;
    stroke?: string;
    strokeDasharray?: string;
  }[];
  getBarColor?: (value: number, key: string) => string;
  headerExtra?: React.ReactNode;
  emptyMessage?: string;
  emptyIsWarning?: boolean;
  minPointSize?: number;
}

const Y_AXIS_WIDTH = 220;
const BAR_SIZE = 32;
const CHART_PADDING = 104;

const HorizontalStackedBarChartCard = ({
  title,
  data,
  yKey,
  bars,
  loading = false,
  error = null,
  xDomain = [0, 100],
  tooltipSuffix = "%",
  stacked = true,
  labelMinThreshold = 8,
  referenceValue,
  referenceLabel,
  referenceLines = [],
  getBarColor,
  headerExtra,
  emptyMessage = "Sense dades disponibles",
  emptyIsWarning = false,
  minPointSize = 0,
}: HorizontalStackedBarChartCardProps) => {
  const chartHeight = data.length > 0
    ? data.length * (BAR_SIZE + 16) + CHART_PADDING
    : 320;
  const containerHeight = Math.max(320, chartHeight);
  const resolvedReferenceLines = [
    ...(typeof referenceValue === "number"
      ? [{ value: referenceValue, label: referenceLabel }]
      : []),
    ...referenceLines,
  ];

  return (
    <div className={styles.card} data-chart-card>
      {headerExtra ? (
        <div className={styles.header}>
          <h2 className={styles.title}>{title}</h2>
          {headerExtra}
        </div>
      ) : (
        <h2 className={styles.title}>{title}</h2>
      )}

      <div style={{ width: "100%", minWidth: 0, height: containerHeight, minHeight: 320 }}>
        {loading && (
          <div className={styles.centered}>
            <Spin size="small" /> Carregant dades…
          </div>
        )}
        {!loading && error && (
          <div className={styles.centered}>
            <p className={styles.errorMessage}>{error}</p>
          </div>
        )}
        {!loading && !error && data.length === 0 && (
          <div className={styles.centered}>
            <p className={emptyIsWarning ? styles.warningNotice : styles.emptyMessage}>
              {emptyIsWarning ? `⚠ ${emptyMessage}` : emptyMessage}
            </p>
          </div>
        )}
        {!loading && !error && data.length > 0 && (
          <ResponsiveContainer width="100%" height={containerHeight}>
            <BarChart
              layout="vertical"
              data={data}
              margin={{ top: 36, right: 32, left: 8, bottom: 8 }}
            >
              <XAxis
                type="number"
                domain={xDomain}
                hide={stacked}
                tick={stacked ? undefined : { fontSize: 11, fill: "#717C8B" }}
                axisLine={!stacked}
              />
              <YAxis
                type="category"
                dataKey={yKey}
                width={Y_AXIS_WIDTH}
                tick={{ fontSize: 11, fill: "#717C8B" }}
                tickLine={false}
                axisLine={false}
              />
              <Tooltip
                formatter={(value, name) => [`${value ?? ""}${tooltipSuffix}`, name ?? ""]}
                contentStyle={{
                  borderRadius: 8,
                  border: "1px solid #E8EDF3",
                  fontSize: 13,
                }}
                cursor={{ fill: "rgba(0,0,0,0.04)" }}
              />
              <Legend
                iconType="circle"
                iconSize={8}
                wrapperStyle={{ fontSize: 12, paddingTop: 8 }}
              />
              {resolvedReferenceLines.map((line) => (
                <ReferenceLine
                  key={`${line.value}-${line.label ?? "reference"}`}
                  x={line.value}
                  stroke={line.stroke ?? "#64748B"}
                  strokeDasharray={line.strokeDasharray ?? "4 4"}
                  label={
                    line.label
                      ? {
                          value: line.label,
                          position: "top",
                          fill: line.stroke ?? "#64748B",
                          fontSize: 12,
                          fontWeight: 700,
                        }
                      : undefined
                  }
                />
              ))}
              {bars.map(({ key, label, color }, idx) => (
                <Bar
                  key={key}
                  dataKey={key}
                  name={label}
                  fill={color}
                  stackId={stacked ? "stack" : undefined}
                  minPointSize={minPointSize}
                  maxBarSize={BAR_SIZE}
                  radius={
                    stacked
                      ? idx === bars.length - 1
                        ? [0, 3, 3, 0]
                        : idx === 0
                          ? [3, 0, 0, 3]
                          : undefined
                      : [0, 3, 3, 0]
                  }
                >
                  {getBarColor &&
                    data.map((point) => {
                      const rawValue = point[key];
                      const numericValue = typeof rawValue === "number" ? rawValue : Number(rawValue);
                      return (
                        <Cell
                          key={`${key}-${point[yKey]}`}
                          fill={Number.isFinite(numericValue) ? getBarColor(numericValue, key) : color}
                        />
                      );
                    })}
                  <LabelList
                    dataKey={key}
                    position={stacked ? "center" : "right"}
                    formatter={(v: unknown) =>
                      typeof v === "number" && v >= labelMinThreshold
                        ? `${v}${tooltipSuffix}`
                        : ""
                    }
                    style={{
                      fontSize: 11,
                      fill: stacked ? "#fff" : "#555",
                      fontWeight: 600,
                    }}
                  />
                </Bar>
              ))}
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
};

export default HorizontalStackedBarChartCard;
